import React, { useState, useEffect, useRef } from 'react';
import { findBreakEvenPrice, findPriceForMargin } from '../lib/calculations';
import NumberStepper from './NumberStepper';

/**
 * ProfitPriceFinderBar — standalone, attention-grabbing bar that sits
 * between the Financial Snapshot row and the rest of the calculator flow.
 *
 * Collapsed: a dark ink banner with an "ADVANCED" tag and a strong headline,
 * intentionally distinct from the cream sketch-cards so users don't miss it.
 *
 * Expanded: a cream body opens beneath the dark header with a clean
 * vertical hierarchy — mode pills → input row → result panels → primary
 * action — instead of the previous flex-wrap-everything chaos.
 */
export default function ProfitPriceFinderBar({
  inputs,
  setInputs,
  settings,
  currencySymbol = '$',
  resetSignal,
}) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('profit'); // 'profit' | 'margin'
  const [targetProfit, setTargetProfit] = useState(inputs?.desiredProfit || '');
  const [targetMargin, setTargetMargin] = useState('30');
  const [breakEven, setBreakEven] = useState({ exact: null, rounded: null });
  const [error, setError] = useState('');
  const [appliedFlash, setAppliedFlash] = useState('');
  const flashTimer = useRef(null);

  // Wipe internal state on a reset signal from the parent
  useEffect(() => {
    setBreakEven({ exact: null, rounded: null });
    setError('');
    setAppliedFlash('');
    setOpen(false);
  }, [resetSignal]);

  // Clear results when the user switches mode
  useEffect(() => {
    setBreakEven({ exact: null, rounded: null });
    setError('');
  }, [mode]);

  useEffect(() => () => { if (flashTimer.current) clearTimeout(flashTimer.current); }, []);

  const findPrice = () => {
    setError('');
    setAppliedFlash('');
    let result;
    if (mode === 'profit') {
      const dp = parseFloat(targetProfit);
      if (!dp || dp <= 0) { setError('Enter a target profit greater than 0.'); return; }
      setInputs(prev => ({ ...prev, desiredProfit: targetProfit }));
      result = findBreakEvenPrice(inputs, settings, dp);
    } else {
      const tm = parseFloat(targetMargin);
      if (!tm || tm <= 0 || tm >= 100) { setError('Margin should be between 1 and 99.'); return; }
      result = findPriceForMargin(inputs, settings, tm);
    }
    if (result) setBreakEven(result);
    else setError("Couldn't find a price that hits that target. Try lowering it.");
  };

  const onKey = (e) => { if (e.key === 'Enter') findPrice(); };

  const applyPrice = () => {
    const next = breakEven.rounded ?? breakEven.exact;
    if (next == null) return;
    const priceStr = next.toFixed(2);
    setInputs(prev => ({ ...prev, price: priceStr }));
    setAppliedFlash(`✓ Price set to ${currencySymbol}${priceStr} — re-calculating...`);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setAppliedFlash(''), 3500);
  };

  // Mode pill renderer
  const modePill = (val, label) => {
    const active = mode === val;
    return (
      <button
        type="button"
        onClick={() => setMode(val)}
        style={{
          padding: '0.4rem 0.95rem',
          fontFamily: 'Inter, sans-serif',
          fontSize: '0.88rem',
          fontWeight: 700,
          cursor: 'pointer',
          border: 'none',
          borderRight: val === 'profit' ? '1px solid rgba(26, 26, 46, 0.12)' : 'none',
          backgroundColor: active ? '#1e3a8a' : '#fefefe',
          color: active ? '#fefefe' : '#1e293b',
          transition: 'all 0.12s',
        }}
      >
        {label}
      </button>
    );
  };

  const hasResult = breakEven.exact != null && !error;

  return (
    <div
      style={{
        // Card-level container — no outer padding so the dark header can run edge-to-edge
        background: '#fefdfa',
        border: '2px solid #1a1a2e',
        borderRadius: '1rem',
        overflow: 'hidden',
        boxShadow:
          '0 2px 0 rgba(26,26,46,0.08), 0 6px 16px rgba(26,26,46,0.06)',
      }}
    >
      {/* ─── Dark header (always visible, clickable to toggle) ─── */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.85rem',
          width: '100%',
          padding: '0.85rem 1.1rem',
          background: 'linear-gradient(180deg, #1e293b 0%, #1a1a2e 100%)',
          color: '#fefdf8',
          border: 'none',
          textAlign: 'left',
          cursor: 'pointer',
          transition: 'background 0.15s',
          fontFamily: 'Inter, sans-serif',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'linear-gradient(180deg, #243144 0%, #20233a 100%)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'linear-gradient(180deg, #1e293b 0%, #1a1a2e 100%)'; }}
      >
        {/* Icon medallion */}
        <span
          aria-hidden="true"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '38px',
            height: '38px',
            borderRadius: '50%',
            background: 'rgba(254, 253, 248, 0.12)',
            border: '1.5px solid rgba(254, 253, 248, 0.25)',
            fontSize: '1.15rem',
            flexShrink: 0,
          }}
        >
          🎯
        </span>

        {/* Headline + subtitle */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '1.02rem', fontWeight: 700, letterSpacing: '-0.005em' }}>
              Find a target price
            </span>
            <span
              style={{
                fontSize: '0.62rem',
                fontWeight: 800,
                letterSpacing: '0.12em',
                padding: '0.12rem 0.45rem',
                background: 'rgba(180, 83, 9, 0.95)',
                color: '#fefdf8',
                borderRadius: '0.3rem',
                lineHeight: 1.2,
              }}
            >
              ADVANCED
            </span>
          </div>
          <p style={{ fontSize: '0.82rem', color: 'rgba(254, 253, 248, 0.75)', margin: '2px 0 0', lineHeight: 1.35 }}>
            Pick a profit or margin goal — we'll work backwards to the listing price.
          </p>
        </div>

        {/* Chevron */}
        <span
          aria-hidden="true"
          style={{
            fontSize: '0.95rem',
            transition: 'transform 0.18s',
            transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
            opacity: 0.85,
            flexShrink: 0,
          }}
        >
          ▶
        </span>
      </button>

      {/* ─── Expanded body ─── */}
      {open && (
        <div style={{ padding: '1rem 1.1rem 1.1rem' }}>
          {/* Step 1: Mode pills */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', marginBottom: '0.85rem', flexWrap: 'wrap' }}>
            <span
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.78rem',
                fontWeight: 700,
                color: 'rgba(26,26,46,0.6)',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              I want to hit:
            </span>
            <div
              style={{
                display: 'inline-flex',
                border: '1.5px solid rgba(26, 26, 46, 0.15)',
                borderRadius: '0.55rem',
                overflow: 'hidden',
                boxShadow: '0 1px 2px rgba(26,26,46,0.05)',
              }}
            >
              {modePill('profit', 'Target profit')}
              {modePill('margin', 'Target margin')}
            </div>
          </div>

          {/* Step 2: Input + Find button */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.6rem', flexWrap: 'wrap' }}>
            <div style={{ flex: '0 0 auto', minWidth: '140px', maxWidth: '180px' }}>
              <label
                style={{
                  display: 'block',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  color: 'rgba(26,26,46,0.75)',
                  marginBottom: '4px',
                }}
              >
                {mode === 'profit'
                  ? `Profit per item (${currencySymbol})`
                  : 'Margin (%)'}
              </label>
              {mode === 'profit' ? (
                <NumberStepper
                  value={targetProfit}
                  onChange={setTargetProfit}
                  step={1}
                  min={0}
                  placeholder="0"
                  inputClassName="sketch-input"
                  onKeyDown={onKey}
                />
              ) : (
                <NumberStepper
                  value={targetMargin}
                  onChange={setTargetMargin}
                  step={5}
                  min={1}
                  max={99}
                  placeholder="30"
                  inputClassName="sketch-input"
                  onKeyDown={onKey}
                />
              )}
            </div>
            <button
              type="button"
              onClick={findPrice}
              style={{
                padding: '0.55rem 1.1rem',
                backgroundColor: '#1e3a8a',
                color: '#fefefe',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: '0.95rem',
                lineHeight: 1.1,
                border: '2px solid #1a1a2e',
                borderRadius: '0.55rem',
                letterSpacing: '0.01em',
                boxShadow: '0 1px 2px rgba(26,26,46,0.06), 0 2px 6px rgba(26,26,46,0.07)',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'all 0.1s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1e40af'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#1e3a8a'; }}
            >
              🎯 Find price →
            </button>
          </div>

          {/* Error */}
          {error && (
            <p
              className="font-note text-note text-alert"
              style={{ marginTop: '0.6rem' }}
            >
              {error}
            </p>
          )}

          {/* Step 3: Result panels (only when we have a result) */}
          {hasResult && (
            <>
              <div
                style={{
                  marginTop: '0.95rem',
                  paddingTop: '0.85rem',
                  borderTop: '1px dashed rgba(26,26,46,0.18)',
                }}
              >
                <p
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.74rem',
                    fontWeight: 700,
                    color: 'rgba(26,26,46,0.55)',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    marginBottom: '0.55rem',
                  }}
                >
                  Suggested price
                </p>
                <div
                  className="finder-results-grid"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                    gap: '0.6rem',
                  }}
                >
                  <ResultPanel
                    label="Exact"
                    value={breakEven.exact}
                    sublabel="precise math, hits the target"
                    accent="#047857"
                    accentBg="#d1fae5"
                    sym={currencySymbol}
                  />
                  <ResultPanel
                    label="Rounded"
                    value={breakEven.rounded}
                    sublabel="buyer-friendly"
                    accent="#b45309"
                    accentBg="#fef3c7"
                    sym={currencySymbol}
                    badge="RECOMMENDED"
                  />
                </div>

                {/* Primary action — full width below the result panels */}
                <button
                  type="button"
                  onClick={applyPrice}
                  style={{
                    marginTop: '0.7rem',
                    width: '100%',
                    padding: '0.65rem 1rem',
                    background: 'linear-gradient(180deg, #047857 0%, #065f46 100%)',
                    color: '#fefdf8',
                    border: '2px solid #1a1a2e',
                    borderRadius: '0.6rem',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    letterSpacing: '0.01em',
                    boxShadow: '0 1px 2px rgba(26,26,46,0.06), 0 3px 8px rgba(4,120,87,0.18)',
                    cursor: 'pointer',
                    transition: 'all 0.1s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'linear-gradient(180deg, #059669 0%, #047857 100%)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'linear-gradient(180deg, #047857 0%, #065f46 100%)'; }}
                >
                  ✓ Use {currencySymbol}{(breakEven.rounded ?? breakEven.exact).toFixed(2)} as my listing price
                </button>

                {/* Context line */}
                <p
                  className="font-note text-note text-ink/72"
                  style={{ marginTop: '0.5rem', textAlign: 'center' }}
                >
                  ~ to net {currencySymbol}{mode === 'profit' ? targetProfit : '?'}{' '}
                  {mode === 'profit' ? 'per item' : `at a ${targetMargin}% margin`}{' '}
                  after fees ~
                </p>
              </div>

              {/* Applied confirmation */}
              {appliedFlash && (
                <div
                  role="status"
                  aria-live="polite"
                  style={{
                    marginTop: '0.6rem',
                    padding: '0.45rem 0.75rem',
                    background: '#d1fae5',
                    border: '1.5px solid #047857',
                    borderRadius: '0.45rem',
                    color: '#065f46',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    textAlign: 'center',
                  }}
                >
                  {appliedFlash}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Result panel sub-component ─────────────────────────────────────
function ResultPanel({ label, value, sublabel, accent, accentBg, sym, badge }) {
  return (
    <div
      style={{
        padding: '0.7rem 0.85rem',
        background: accentBg,
        border: `1.5px solid ${accent}`,
        borderRadius: '0.6rem',
        position: 'relative',
        textAlign: 'center',
        minWidth: 0,
      }}
    >
      {badge && (
        <div
          style={{
            position: 'absolute',
            top: '-9px',
            right: '0.6rem',
            background: accent,
            color: '#fefdf8',
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.6rem',
            fontWeight: 800,
            padding: '0.12rem 0.42rem',
            borderRadius: '0.3rem',
            letterSpacing: '0.08em',
          }}
        >
          {badge}
        </div>
      )}
      <p
        style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '0.66rem',
          fontWeight: 700,
          color: accent,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          margin: '0 0 0.2rem',
        }}
      >
        {label}
      </p>
      <p
        className="font-numbers"
        style={{
          fontSize: '1.7rem',
          fontWeight: 700,
          color: accent === '#b45309' ? '#7c2d12' : '#065f46',
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '-0.02em',
          lineHeight: 1.05,
          margin: '0 0 0.2rem',
        }}
      >
        {sym}{value.toFixed(2)}
      </p>
      <p
        style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '0.72rem',
          color: 'rgba(26,26,46,0.7)',
          lineHeight: 1.3,
          margin: 0,
        }}
      >
        {sublabel}
      </p>
    </div>
  );
}
